// One form row, rendered by the four-state machine (empty / suggested /
// confirmed / invalid). Confirmed scalar rows collapse to a dense summary that
// re-expands on tap; checklist and consent stay expanded. Suggested rows carry
// the AI chip with Confirm/Undo; invalid rows show their error as an alert and
// never collapse or count toward progress.
//
// Per-field help (slice 5): the Ask-AI control sits on the label row of a
// VISIBLE field (design D6/D7 — the caller decides visibility, see
// FormOverlay's `helpAffordance`, since it needs `openForAI` and review-mode
// state this row doesn't have). "Visible" is broader than "eligible to send
// in" (adversarial-review B1a/R2): a field that's confirmed or hidden
// mid-thread keeps its control reachable — while the thread is open, or via
// a tap-to-expand once history exists (except a pending suggestion, which
// Confirm/Undo owns unconditionally) — so the conversation stays reopenable;
// `help.lockedMessage`, when present, is what the thread renders to explain
// why the composer won't send. The row itself only stays expanded past a
// fill while the thread is OPEN (adversarial-review R3 — a confirmed scalar
// must still earn its collapsed summary; the control reappears on tap, since
// `help.visible` survives the collapse even though the row doesn't stay
// expanded for it). Activating the control reflects an open state on itself;
// the inline host variant renders its thread panel beneath this row's own
// content, behind the `HELP_HOST` build constant — the sheet variant mounts
// once at the overlay level instead (mirrors `PickerSheet`).

import { AddressBag } from './addressFill';
import { FieldInput } from './FieldControls';
import styles from './ConversationalForms.module.css';
import FieldHelpInline from './FieldHelpInline';
import { HelpMessage } from './FieldHelpThread';
import { HELP_HOST } from './helpHostConfig';
import { AlertIcon, PencilIcon, SparklesIcon } from './icons';
import { CfField, FieldStatus, FieldValue } from './types';
import { displayValue } from './valueFormat';

export type FieldRowHelp = {
    /** The Ask-AI control renders (design D6/D7 + adversarial-review B1a/R2):
     *  currently extraction-eligible, OR its thread is open, OR (unless the
     *  field is mid-suggestion) its thread has session history to reopen —
     *  never in review mode, never for `noAI`/`consent`. The row itself
     *  doesn't need to know WHICH of those is true, only the result — see
     *  `FormOverlay.helpAffordance` for the full computation. */
    visible: boolean;
    /** Present when the field isn't currently extraction-eligible — the
     *  thread stays viewable but the composer locks with this explanation
     *  (adversarial-review B1b). */
    lockedMessage: string | undefined;
    /** This field's thread is the one currently open. */
    open: boolean;
    sectionTitle: string;
    thread: HelpMessage[];
    busy: boolean;
    slow: boolean;
    onOpen: (fieldId: string) => void;
    onClose: () => void;
    /** Returns `false` when the send-time guard refused it (adversarial-
     *  review R6) — the composer only clears its draft on `true`. Resolves
     *  SYNCHRONOUSLY (adversarial-review N1); the turn itself runs detached. */
    onSend: (fieldId: string, text: string) => boolean;
    onRetry: (fieldId: string, index: number, text: string) => void;
};

/** Types whose confirmed rows collapse. Multi-part types stay expanded. */
const COLLAPSIBLE: ReadonlySet<string> = new Set([
    'text', 'email', 'date', 'monthyear', 'daterange', 'number',
    'phone', 'zip', 'ssn', 'select', 'radio', 'address',
]);

type FieldRowProps = {
    field: CfField;
    /** Render the conditional-child treatment (inset + indent). The caller
     *  decides, because it reads as "conditional on the row above" and only an
     *  in-form parent has a row above — a cross-form-parented field is
     *  conditional too, but renders plain (tracker 6.26b). */
    asChild?: boolean;
    value: FieldValue | undefined;
    suggestion: FieldValue | undefined;
    status: FieldStatus;
    error: string | undefined;
    editing: boolean;
    onChange: (fieldId: string, value: FieldValue) => void;
    onConfirm: (fieldId: string) => void;
    onUndo: (fieldId: string) => void;
    onEdit: (fieldId: string) => void;
    onDoneEdit: (fieldId: string) => void;
    onOpenPicker: (fieldId: string) => void;
    /** A Google Places pick on an address field — the overlay multi-fills. */
    onAddressPick?: (fieldId: string, bag: AddressBag) => void;
    /** Field-help wiring (slice 5). Absent only for callers that predate the
     *  feature (there are none in this repo, but the type stays optional so a
     *  row can be exercised without it in isolation). */
    help?: FieldRowHelp;
};

const FieldRow = ({
    field,
    asChild,
    value,
    suggestion,
    status,
    error,
    editing,
    onChange,
    onConfirm,
    onUndo,
    onEdit,
    onDoneEdit,
    onOpenPicker,
    onAddressPick,
    help,
}: FieldRowProps) => {
    // Field help (slice 5): a fill landing through the thread commits the
    // field, which would ordinarily collapse this row right out from under an
    // open conversation — including the receipt the reply just added. Stay
    // expanded while the thread is OPEN so a fill doesn't strand it
    // mid-conversation. Once closed, this collapses like any other confirmed
    // scalar row (adversarial-review R3 — the form-view contract requires
    // that; a field ever touched by help must still earn its dense summary).
    // A tap re-expands it (existing `onEdit` behavior below), and the control
    // reappears there since `help.visible` outlives the collapse — so the
    // thread stays reachable, just one tap further than while it was open.
    // Losing the control's DOM node this way is exactly why the hosts now
    // carry their own row-focus fallback for the close-time restore (M1/R3).
    const collapsed = status === 'confirmed' && !editing && !help?.open && COLLAPSIBLE.has(field.type);

    if (collapsed) {
        return (
            <button
                type="button"
                className={`${styles.collapsedRow} ${asChild ? styles.childRow : ''}`}
                onClick={() => onEdit(field.id)}
                data-testid={`cf-row-${field.id}`}
                aria-label={`${field.label}: ${displayValue(field, value)} — edit`}
            >
                <span className={styles.collapsedCheck} aria-hidden="true">✓</span>
                <span className={styles.collapsedLabel}>{field.label}</span>
                <span className={styles.collapsedValue}>{displayValue(field, value)}</span>
                <span className={styles.collapsedEdit} aria-hidden="true">
                    <PencilIcon />
                </span>
            </button>
        );
    }

    const shownValue = status === 'suggested' ? suggestion : value;
    const rowStateClass =
        status === 'suggested' ? styles.rowSuggested : error ? styles.rowError : '';

    const handleChange = (next: FieldValue) => {
        onChange(field.id, next);
        // Choosing closes the editor for single-choice controls; text closes on blur.
        if (editing && (field.type === 'radio' || field.type === 'select')) {
            onDoneEdit(field.id);
        }
    };

    return (
        <div
            className={`${styles.fieldRow} ${rowStateClass} ${asChild ? styles.childRow : ''}`}
            data-testid={`cf-row-${field.id}`}
            // Programmatic-only focus target (adversarial-review N2): the
            // collapsed row is a real `<button>`, but this expanded form is a
            // plain `<div>` with nothing to focus by default — the hosts'
            // close-time row-focus fallback needs SOME element here to land
            // on (a suggested-mode or hidden-field close never collapses the
            // row), and `-1` keeps it out of the normal Tab order.
            tabIndex={-1}
        >
            <div className={styles.fieldTop}>
                <span className={styles.fieldLabel}>
                    {field.label}
                    {field.optional ? (
                        <span className={styles.optionalTag}> · optional</span>
                    ) : (
                        <span className={styles.requiredStar} aria-hidden="true"> *</span>
                    )}
                </span>
                {status === 'suggested' && (
                    <span className={styles.aiChip}>
                        <SparklesIcon /> AI
                    </span>
                )}
                {help?.visible && (
                    <button
                        type="button"
                        className={`${styles.askHelp} ${help.open ? styles.askHelpOpen : ''}`}
                        onClick={(e) => {
                            // Live-verification regression: suppressing
                            // mousedown's default focus-shift (below) means a
                            // POINTER activation never actually focuses this
                            // button — so `useFocusTrap`'s `prev` capture at
                            // sheet-mount time would grab whatever had focus
                            // BEFORE the click (e.g. the overlay's "Back to
                            // chat"), and closing would restore focus there
                            // instead of here, violating the delta spec's
                            // "closing SHALL restore focus to the Ask-AI
                            // control". Focus this control explicitly, in the
                            // SAME handler, before opening — `openHelp`'s own
                            // state update batches with any `editing`-false
                            // update a blur here triggers (React 18 automatic
                            // batching applies across synchronous calls
                            // within one event handler), so a re-expanded
                            // confirmed row still re-renders with
                            // `editing=false` AND `help.open=true` in one
                            // commit — the collapse guard's `!help?.open`
                            // keeps it expanded regardless, and this button
                            // stays mounted for the trap to capture.
                            e.currentTarget.focus();
                            help.onOpen(field.id);
                        }}
                        // On a re-expanded CONFIRMED row (tap-to-edit, input
                        // auto-focused), a plain mousedown here would blur
                        // that input first (`onDoneEdit` fires) — which
                        // flips `editing` false and collapses the row (and
                        // unmounts this button) before mouseup/click ever
                        // fires, silently swallowing the press. Suppressing
                        // mousedown's default focus-shift keeps the click
                        // landing; `onClick` above does the (now necessary)
                        // explicit focus itself. Keyboard activation
                        // (Enter/Space on an already-focused control) never
                        // triggers `mousedown` at all, so it was never
                        // affected by either the original defect or this.
                        onMouseDown={(e) => e.preventDefault()}
                        aria-pressed={help.open}
                        aria-label={`Ask about ${field.label}`}
                        data-testid={`cf-ask-help-${field.id}`}
                    >
                        <SparklesIcon /> Ask AI
                    </button>
                )}
            </div>

            <FieldInput
                field={field}
                value={shownValue}
                onChange={handleChange}
                onFocus={() => onEdit(field.id)}
                onBlur={() => onDoneEdit(field.id)}
                onOpenPicker={onOpenPicker}
                onAddressPick={onAddressPick}
                autoFocus={editing && COLLAPSIBLE.has(field.type) && field.type !== 'select' && field.type !== 'radio'}
            />

            {error ? (
                <div className={styles.fieldError} role="alert">
                    <AlertIcon /> {error}
                </div>
            ) : field.help ? (
                <div className={styles.fieldHelp} id={`${field.id}-help`}>
                    {field.help}
                </div>
            ) : null}

            {status === 'suggested' && (
                <div className={styles.suggestActions}>
                    <button
                        type="button"
                        className={styles.suggestConfirm}
                        onClick={() => onConfirm(field.id)}
                    >
                        ✓ Confirm
                    </button>
                    <button type="button" className={styles.suggestUndo} onClick={() => onUndo(field.id)}>
                        Undo
                    </button>
                </div>
            )}

            {HELP_HOST === 'inline' && help?.open && (
                <FieldHelpInline
                    field={field}
                    sectionTitle={help.sectionTitle}
                    thread={help.thread}
                    busy={help.busy}
                    slow={help.slow}
                    onSend={(text) => help.onSend(field.id, text)}
                    onRetry={(index, text) => help.onRetry(field.id, index, text)}
                    onClose={help.onClose}
                    lockedMessage={help.lockedMessage}
                />
            )}
        </div>
    );
};

export default FieldRow;
